import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, X, MapPin, ChevronUp, ChevronDown, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card, Btn, Loader } from "@/components/portal/ui";

const LANGS = ["de", "fr", "it", "en"];
const TYPES = ["country", "canton", "region", "city"];
const emptyI18n = { de: "", fr: "", it: "", en: "" };
const emptyForm = {
  slug: "", name: { ...emptyI18n }, type: "city", parent_id: "", postal_code: "", lat: "", lng: "",
  description: { ...emptyI18n }, meta_title: { ...emptyI18n }, meta_description: { ...emptyI18n }, active: true, visible: true,
};

function typeLabel(tp, L) {
  const m = {
    country: { de: "Land", fr: "Pays", it: "Paese", en: "Country" },
    canton: { de: "Kanton", fr: "Canton", it: "Cantone", en: "Canton" },
    region: { de: "Region", fr: "Région", it: "Regione", en: "Region" },
    city: { de: "Ort/Stadt", fr: "Ville", it: "Città", en: "City" },
  };
  return L(m[tp] || { de: tp });
}

function Switch({ on, onClick, testid, disabled }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled} aria-pressed={on} type="button"
      className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50"
      style={{ background: on ? "var(--accent)" : "var(--surface-alt)" }}>
      <span className="inline-block h-4 w-4 rounded-full bg-white shadow transition-transform"
        style={{ transform: on ? "translateX(24px)" : "translateX(4px)" }} />
    </button>
  );
}

function I18nField({ label, value, onChange, textarea, testid }) {
  const st = { background: "var(--bg)", borderColor: "var(--border)", borderRadius: "var(--radius)", color: "var(--text)" };
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{label}</label>
      <div className="grid grid-cols-2 gap-2 mt-1">
        {LANGS.map((lng) => (
          <div key={lng} className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] font-bold uppercase" style={{ color: "var(--text-muted)" }}>{lng}</span>
            {textarea ? (
              <textarea data-testid={`${testid}-${lng}`} rows={2} className="w-full pl-8 pr-2 py-2 text-sm border outline-none" style={st}
                value={value?.[lng] || ""} onChange={(e) => onChange({ ...value, [lng]: e.target.value })} />
            ) : (
              <input data-testid={`${testid}-${lng}`} className="w-full pl-8 pr-2 h-10 text-sm border outline-none" style={st}
                value={value?.[lng] || ""} onChange={(e) => onChange({ ...value, [lng]: e.target.value })} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function LocationsManager() {
  const { t, L } = useI18n();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/locations");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const nameOf = (id) => { const l = (items || []).find((x) => x.id === id); return l ? (L(l.name) || l.slug) : ""; };

  const openNew = () => { setForm({ ...emptyForm, name: { ...emptyI18n }, description: { ...emptyI18n }, meta_title: { ...emptyI18n }, meta_description: { ...emptyI18n } }); setEditing("new"); };
  const openEdit = (l) => {
    setForm({
      slug: l.slug || "", name: { ...emptyI18n, ...(l.name || {}) }, type: l.type || "city",
      parent_id: l.parent_id || "", postal_code: l.postal_code || "",
      lat: l.lat != null ? String(l.lat) : "", lng: l.lng != null ? String(l.lng) : "",
      description: { ...emptyI18n, ...(l.description || {}) },
      meta_title: { ...emptyI18n, ...(l.meta_title || {}) },
      meta_description: { ...emptyI18n, ...(l.meta_description || {}) },
      active: l.active !== false, visible: l.visible !== false,
    });
    setEditing(l.id);
  };
  const close = () => { setEditing(null); setForm(emptyForm); };

  const payload = () => ({
    ...form,
    lat: form.lat === "" ? null : parseFloat(form.lat),
    lng: form.lng === "" ? null : parseFloat(form.lng),
    parent_id: form.parent_id || null,
  });

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") await api.post("/admin/locations", payload());
      else await api.put(`/admin/locations/${editing}`, payload());
      toast.success(t("common.saved"));
      close();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const quickToggle = async (l, field) => {
    try {
      await api.put(`/admin/locations/${l.id}`, {
        slug: l.slug, name: l.name, type: l.type, parent_id: l.parent_id || null,
        postal_code: l.postal_code, lat: l.lat, lng: l.lng,
        description: l.description, meta_title: l.meta_title, meta_description: l.meta_description,
        active: field === "active" ? !(l.active !== false) : l.active !== false,
        visible: field === "visible" ? !(l.visible !== false) : l.visible !== false,
      });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const move = async (idx, dir) => {
    const arr = [...items];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setItems(arr);
    try { await api.post("/admin/locations/reorder", { ids: arr.map((l) => l.id) }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };

  const remove = async (l) => {
    if (!window.confirm(L({ de: "Standort wirklich löschen?", fr: "Supprimer le lieu ?", it: "Eliminare la località?", en: "Delete this location?" }))) return;
    try {
      const { data } = await api.delete(`/admin/locations/${l.id}`);
      if (data.in_use) toast.warning(L({ de: `Gelöscht – war ${data.in_use} Anbietern zugewiesen.`, fr: `Supprimé – assigné à ${data.in_use} prestataires.`, it: `Eliminato – assegnato a ${data.in_use} fornitori.`, en: `Deleted – was assigned to ${data.in_use} providers.` }));
      else toast.success(t("common.saved"));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const st = { background: "var(--bg)", borderColor: "var(--border)", borderRadius: "var(--radius)", color: "var(--text)" };
  if (!items) return <Loader />;
  const parents = items.filter((l) => l.id !== editing);

  return (
    <div className="space-y-4" data-testid="locations-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>{items.length} · {L({ de: "Standorte", fr: "Lieux", it: "Località", en: "Locations" })}</p>
        <Btn onClick={openNew} data-testid="loc-new-btn"><Plus className="h-4 w-4" /> {L({ de: "Neuer Standort", fr: "Nouveau lieu", it: "Nuova località", en: "New location" })}</Btn>
      </div>

      {items.length === 0 && (
        <Card className="p-12 flex flex-col items-center gap-3 text-center" data-testid="loc-empty">
          <MapPin className="h-8 w-8" style={{ color: "var(--accent)" }} />
          <span style={{ color: "var(--text-muted)" }}>{L({ de: "Noch keine Standorte.", fr: "Aucun lieu.", it: "Nessuna località.", en: "No locations yet." })}</span>
        </Card>
      )}

      <div className="space-y-2">
        {items.map((l, idx) => (
          <Card key={l.id} className="p-3 flex flex-wrap items-center gap-3 kw-fade-up" data-testid={`loc-item-${l.id}`}>
            <div className="flex flex-col">
              <button onClick={() => move(idx, -1)} disabled={idx === 0} className="disabled:opacity-30" data-testid={`loc-up-${l.id}`}><ChevronUp className="h-4 w-4" style={{ color: "var(--text-muted)" }} /></button>
              <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="disabled:opacity-30" data-testid={`loc-down-${l.id}`}><ChevronDown className="h-4 w-4" style={{ color: "var(--text-muted)" }} /></button>
            </div>
            <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full whitespace-nowrap" style={{ background: "var(--surface-alt)", color: "var(--accent)" }}>{typeLabel(l.type, L)}</span>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate flex items-center gap-2" style={{ color: "var(--text)" }}>
                {L(l.name) || l.slug}
                {l.active === false && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded-full" style={{ background: "var(--surface-alt)", color: "var(--text-muted)" }}>{L({ de: "inaktiv", fr: "inactif", it: "inattivo", en: "inactive" })}</span>}
              </div>
              <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>
                /{l.slug}{l.parent_id ? ` · ${L({ de: "in", fr: "dans", it: "in", en: "in" })} ${nameOf(l.parent_id)}` : ""}{l.postal_code ? ` · ${l.postal_code}` : ""}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5" title={L({ de: "Aktiv", fr: "Actif", it: "Attivo", en: "Active" })}>
                <span className="text-[10px] uppercase" style={{ color: "var(--text-muted)" }}>{L({ de: "Aktiv", fr: "Actif", it: "Attivo", en: "Active" })}</span>
                <Switch testid={`loc-active-${l.id}`} on={l.active !== false} onClick={() => quickToggle(l, "active")} />
              </div>
              <button onClick={() => quickToggle(l, "visible")} data-testid={`loc-visible-${l.id}`} title={L({ de: "Sichtbarkeit", fr: "Visibilité", it: "Visibilità", en: "Visibility" })}
                className="h-9 w-9 flex items-center justify-center" style={{ borderRadius: "var(--radius)", background: "var(--surface-alt)" }}>
                {l.visible !== false ? <Eye className="h-4 w-4" style={{ color: "var(--accent)" }} /> : <EyeOff className="h-4 w-4" style={{ color: "var(--text-muted)" }} />}
              </button>
              <Btn variant="soft" onClick={() => openEdit(l)} className="h-9 px-3 text-xs" data-testid={`loc-edit-${l.id}`}><Pencil className="h-3.5 w-3.5" /></Btn>
              <Btn variant="danger" onClick={() => remove(l)} className="h-9 px-3" data-testid={`loc-delete-${l.id}`}><Trash2 className="h-3.5 w-3.5" /></Btn>
            </div>
          </Card>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,.5)" }} data-testid="loc-modal">
          <Card className="w-full max-w-2xl p-6 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold flex items-center gap-2" style={{ color: "var(--text)" }}><MapPin className="h-5 w-5" style={{ color: "var(--accent)" }} /> {editing === "new" ? L({ de: "Neuer Standort", fr: "Nouveau lieu", it: "Nuova località", en: "New location" }) : L({ de: "Standort bearbeiten", fr: "Modifier le lieu", it: "Modifica località", en: "Edit location" })}</h3>
              <button onClick={close} data-testid="loc-modal-close"><X className="h-5 w-5" style={{ color: "var(--text-muted)" }} /></button>
            </div>
            <div className="space-y-4">
              <I18nField testid="loc-name" label={L({ de: "Name", fr: "Nom", it: "Nome", en: "Name" })} value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{L({ de: "Typ", fr: "Type", it: "Tipo", en: "Type" })}</label>
                  <select data-testid="loc-type" className="w-full px-3.5 h-10 text-sm border outline-none mt-1" style={st}
                    value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                    {TYPES.map((tp) => <option key={tp} value={tp}>{typeLabel(tp, L)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{L({ de: "Übergeordnet", fr: "Parent", it: "Superiore", en: "Parent" })}</label>
                  <select data-testid="loc-parent" className="w-full px-3.5 h-10 text-sm border outline-none mt-1" style={st}
                    value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })}>
                    <option value="">—</option>
                    {parents.map((p) => <option key={p.id} value={p.id}>{typeLabel(p.type, L)}: {L(p.name) || p.slug}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>PLZ</label>
                  <input data-testid="loc-plz" className="w-full px-3.5 h-10 text-sm border outline-none mt-1" style={st}
                    value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Lat</label>
                  <input data-testid="loc-lat" type="number" step="any" className="w-full px-3.5 h-10 text-sm border outline-none mt-1" style={st}
                    value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Lng</label>
                  <input data-testid="loc-lng" type="number" step="any" className="w-full px-3.5 h-10 text-sm border outline-none mt-1" style={st}
                    value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Slug</label>
                <input data-testid="loc-slug" placeholder="auto" className="w-full px-3.5 h-10 text-sm border outline-none mt-1" style={st}
                  value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
              </div>
              <div className="pt-2 text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>SEO</div>
              <I18nField testid="loc-metatitle" label="Meta Title" value={form.meta_title} onChange={(v) => setForm({ ...form, meta_title: v })} />
              <I18nField testid="loc-metadesc" label="Meta Description" value={form.meta_description} onChange={(v) => setForm({ ...form, meta_description: v })} textarea />
              <div className="flex items-center gap-6 pt-1">
                <div className="flex items-center gap-2"><span className="text-sm" style={{ color: "var(--text)" }}>{L({ de: "Aktiv", fr: "Actif", it: "Attivo", en: "Active" })}</span><Switch testid="loc-form-active" on={form.active} onClick={() => setForm({ ...form, active: !form.active })} /></div>
                <div className="flex items-center gap-2"><span className="text-sm" style={{ color: "var(--text)" }}>{L({ de: "Sichtbar", fr: "Visible", it: "Visibile", en: "Visible" })}</span><Switch testid="loc-form-visible" on={form.visible} onClick={() => setForm({ ...form, visible: !form.visible })} /></div>
              </div>
              <Btn onClick={save} disabled={saving || !form.name.de} className="w-full" data-testid="loc-save-btn">{t("common.save")}</Btn>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
